/**
 * AdminSubscriptions.tsx — cross-tenant listing subskrypcji modułów (F4B T4B.2, T4B.3).
 *
 * Frontend dopełnienie F4 (która była backend-only). Pokazuje wszystkie subskrypcje
 * w systemie z modal'em ze szczegółami.
 *
 * Model filtrowania „jak w katalogu modulow" (poprawki-develop): ZERO gornej
 * wyszukiwarki i gornych dropdownow filtrow — cale filtrowanie zyje w naglowkach
 * kolumn (wspoldzielony ConfigurableTable, client-side na pobranej liscie).
 * Kolumny wolnotekstowe (Klient/Moduł) maja szukajke; Status — dropdown wyboru.
 * Nad tabela zostaje tylko kontrolka, ktora ZMIENIA pobierany zbior i nie ma
 * odpowiednika w kolumnie: „Pokaż usunięte". Pobieramy do gornego limitu (100) —
 * filtry per kolumna obejmuja pobrana liste.
 *
 * Read-only — zero akcji modyfikacyjnych w F4B (te są w drawer Klienta zakładce „Moduły"
 * przez F4 endpointy /api/admin/tenants/:id/modules/:code/{activate,deactivate,upgrade-seats}).
 *
 * Plan: docs/phases/archive/PHASE_4B_PLAN.md.
 */

import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Repeat } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

export interface SubscriptionListItem {
  id: string;
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'suspended' | string;
  seats: number | null;
  billingCycle: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  activatedAt: string | null;
  cancelledAt: string | null;
  priceMonthly: number | null;
  priceYearly: number | null;
  bundleCode: string | null;
  appliedDiscountPercent: number | null;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  tenant: {
    id: string;
    code: string;
    slug: string;
    name: string;
  };
  module: {
    code: string;
    name: string;
    isCore: boolean;
  };
}

interface SubscriptionListResponse {
  subscriptions: SubscriptionListItem[];
  total: number;
  limit: number;
  offset: number;
}

const STATUS_TO_COLOR: Record<string, BadgeColor> = {
  trialing: 'blue',
  active: 'green',
  past_due: 'amber',
  suspended: 'red',
  cancelled: 'gray',
};

/** Gorny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

/** Kolejnosc statusow — opcje filtra select w kolumnie „Status". */
const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'suspended', 'cancelled'] as const;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatPrice(grosze: number | null): string {
  if (grosze === null || grosze === undefined) return '—';
  const zlote = grosze / 100;
  return zlote.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function AdminSubscriptions() {
  const { t } = useTranslation();
  // Kontrolka zmieniajaca pobierany zbior (bez odpowiednika w kolumnie).
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);
  const [selected, setSelected] = useState<SubscriptionListItem | null>(null);

  const subscriptionsQuery = useQuery<SubscriptionListResponse>({
    queryKey: ['admin', 'subscriptions', { includeDeleted }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (includeDeleted) params.set('includeDeleted', 'true');
      params.set('limit', String(LIST_FETCH_LIMIT));
      const qs = params.toString();
      const res = await api.get<SubscriptionListResponse>(
        `/admin/subscriptions${qs ? '?' + qs : ''}`,
      );
      return res.data;
    },
  });

  const rows = useMemo(
    () => subscriptionsQuery.data?.subscriptions ?? [],
    [subscriptionsQuery.data?.subscriptions],
  );

  const statusLabel = useCallback(
    (status: string): string => {
      const key = `admin.subscriptions.status.${status}`;
      const translated = t(key);
      return translated === key ? status : translated;
    },
    [t],
  );

  const statusFilterOptions = useMemo(
    () => SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: statusLabel(s) })),
    [statusLabel],
  );

  const columns = useMemo<ColumnDefinition<SubscriptionListItem>[]>(
    () => [
      {
        id: 'tenant',
        // Nazwa + kod + slug zlaczone — szukajka tekstowa dziala substring po wszystkich trzech.
        accessor: (row) => `${row.tenant.name} ${row.tenant.code} ${row.tenant.slug}`,
        header: t('admin.subscriptions.columns.tenant'),
        sortable: true,
        renderCell: (_value, row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">
              {row.tenant.name}
              {row.gcrecord && (
                <Badge color="gray" className="ml-2">
                  {t('admin.subscriptions.deletedBadge')}
                </Badge>
              )}
            </span>
            <span className="font-mono text-xs text-text-tertiary">
              {row.tenant.code} · {row.tenant.slug}
            </span>
          </div>
        ),
      },
      {
        id: 'module',
        // Kod + nazwa modulu — szukajka tekstowa po obu.
        accessor: (row) => `${row.module.code} ${row.module.name}`,
        header: t('admin.subscriptions.columns.module'),
        sortable: true,
        renderCell: (_value, row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">{row.module.name}</span>
            <span className="font-mono text-xs text-text-tertiary">{row.module.code}</span>
          </div>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.subscriptions.columns.status'),
        sortable: true,
        // Stany rozłączne → dropdown wyboru w wierszu filtrów (client-side, exact-match).
        headerFilter: { category: 'select', options: statusFilterOptions },
        renderCell: (_value, row) => {
          const color = STATUS_TO_COLOR[row.status] ?? 'gray';
          return <Badge color={color}>{statusLabel(row.status)}</Badge>;
        },
      },
      {
        id: 'seats',
        accessor: (row) => row.seats ?? -1,
        header: t('admin.subscriptions.columns.seats'),
        sortable: true,
        // Liczba — bez szukajki (fraza nie ma sensu).
        filterable: false,
        align: 'right',
        renderCell: (_value, row) => (
          <span className="text-text-secondary">{row.seats !== null ? row.seats : '—'}</span>
        ),
      },
      {
        id: 'period',
        accessor: (row) => row.currentPeriodStart ?? '',
        header: t('admin.subscriptions.columns.period'),
        // Zakres dat — bez szukajki.
        filterable: false,
        renderCell: (_value, row) => {
          if (row.status === 'trialing' && row.trialEndsAt) {
            return (
              <span className="text-xs text-text-secondary">
                {t('admin.subscriptions.trialUntil', { date: formatDate(row.trialEndsAt) })}
              </span>
            );
          }
          if (row.currentPeriodStart && row.currentPeriodEnd) {
            return (
              <span className="text-xs text-text-secondary">
                {formatDate(row.currentPeriodStart)} – {formatDate(row.currentPeriodEnd)}
              </span>
            );
          }
          return <span className="text-xs text-text-muted">—</span>;
        },
      },
      {
        id: 'price',
        accessor: (row) => row.priceMonthly ?? 0,
        header: t('admin.subscriptions.columns.price'),
        sortable: true,
        // Cena — bez szukajki.
        filterable: false,
        align: 'right',
        renderCell: (_value, row) => (
          <div className="flex flex-col text-xs text-text-secondary">
            <span>
              {formatPrice(row.priceMonthly)} {t('admin.subscriptions.priceMonthlySuffix')}
            </span>
            {row.priceYearly !== null && (
              <span className="text-text-tertiary">
                {formatPrice(row.priceYearly)} {t('admin.subscriptions.priceYearlySuffix')}
              </span>
            )}
          </div>
        ),
      },
    ],
    [t, statusLabel, statusFilterOptions],
  );

  const renderActions = useCallback(
    (row: SubscriptionListItem) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setSelected(row);
        }}
        data-testid={`subscription-details-${row.id}`}
      >
        {t('admin.subscriptions.details')}
      </Button>
    ),
    [t],
  );

  return (
    <Layout title={t('admin.subscriptions.title')}>
      <section className="px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">
            {t('admin.subscriptions.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.subscriptions.subtitle')}</p>
        </div>

        {/* Kontrolka zmieniajaca pobierany zbior (reszta filtrow zyje w naglowkach kolumn). */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <Checkbox
            label={t('admin.subscriptions.filters.includeDeletedLabel')}
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            data-testid="subscriptions-filter-deleted"
          />
        </div>

        {subscriptionsQuery.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="subscriptions-loading">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : subscriptionsQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.subscriptions.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void subscriptionsQuery.refetch()}
              data-testid="subscriptions-retry"
            >
              {t('admin.subscriptions.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<SubscriptionListItem>
                tableId="admin-subscriptions"
                testIdPrefix="admin-subscriptions"
                columns={columns}
                data={rows}
                getRowId={(row) => row.id}
                renderActions={renderActions}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={Repeat}
                emptyTitle={
                  rows.length === 0
                    ? t('admin.subscriptions.emptyNoData')
                    : t('admin.subscriptions.emptyFiltered')
                }
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>
            {subscriptionsQuery.data && (
              <p className="mt-3 text-xs text-text-muted" data-testid="subscriptions-count">
                {t('admin.subscriptions.countLabel', { count: rows.length })}
                {subscriptionsQuery.data.total > rows.length &&
                  ' ' +
                    t('admin.subscriptions.countLabelTotal', {
                      total: subscriptionsQuery.data.total,
                    })}
              </p>
            )}
          </>
        )}
      </section>

      {selected && (
        <SubscriptionDetailModal subscription={selected} onClose={() => setSelected(null)} />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Modal szczegółów subskrypcji (T4B.3).
// Read-only — pełne dane subskrypcji + cross-link do drawer Klienta.
// ---------------------------------------------------------------------------

interface SubscriptionDetailModalProps {
  subscription: SubscriptionListItem;
  onClose: () => void;
}

function SubscriptionDetailModal({ subscription: s, onClose }: SubscriptionDetailModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const color = STATUS_TO_COLOR[s.status] ?? 'gray';
  const statusKey = `admin.subscriptions.status.${s.status}`;
  const translatedStatus = t(statusKey);
  const statusLabel = translatedStatus === statusKey ? s.status : translatedStatus;

  function handleOpenInTenantPanel() {
    onClose();
    navigate(`/tenants/${s.tenant.id}`);
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose}>
        {t('admin.subscriptions.modal.close')}
      </Button>
      <Button
        variant="primary"
        onClick={handleOpenInTenantPanel}
        data-testid="subscription-modal-open-tenant"
      >
        {t('admin.subscriptions.modal.openInTenant')}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.subscriptions.modal.title')}
      description={t('admin.subscriptions.modal.description')}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col gap-5 text-sm" data-testid="subscription-detail-modal">
        {/* Klient */}
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('admin.subscriptions.modal.tenantSection')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailField label={t('admin.subscriptions.modal.tenantName')} value={s.tenant.name} />
            <DetailField
              label={t('admin.subscriptions.modal.tenantCode')}
              value={<span className="font-mono">{s.tenant.code}</span>}
            />
            <DetailField
              label={t('admin.subscriptions.modal.tenantSlug')}
              value={<span className="font-mono">{s.tenant.slug}</span>}
            />
          </div>
        </section>

        {/* Moduł */}
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('admin.subscriptions.modal.moduleSection')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailField
              label={t('admin.subscriptions.modal.moduleCode')}
              value={<span className="font-mono">{s.module.code}</span>}
            />
            <DetailField
              label={t('admin.subscriptions.modal.moduleName')}
              value={
                <span>
                  {s.module.name}
                  {s.module.isCore && (
                    <Badge color="accent" className="ml-2">
                      {t('admin.subscriptions.modal.coreBadge')}
                    </Badge>
                  )}
                </span>
              }
            />
          </div>
        </section>

        {/* Subskrypcja */}
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('admin.subscriptions.modal.subscriptionSection')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailField
              label={t('admin.subscriptions.modal.status')}
              value={<Badge color={color}>{statusLabel}</Badge>}
            />
            <DetailField
              label={t('admin.subscriptions.modal.seats')}
              value={s.seats !== null ? String(s.seats) : '—'}
            />
            <DetailField
              label={t('admin.subscriptions.modal.billingCycle')}
              value={s.billingCycle ?? '—'}
            />
            <DetailField
              label={t('admin.subscriptions.modal.trialEndsAt')}
              value={formatDate(s.trialEndsAt)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.currentPeriodStart')}
              value={formatDate(s.currentPeriodStart)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.currentPeriodEnd')}
              value={formatDate(s.currentPeriodEnd)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.activatedAt')}
              value={formatDate(s.activatedAt)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.cancelledAt')}
              value={formatDate(s.cancelledAt)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.priceMonthly')}
              value={
                s.priceMonthly !== null
                  ? `${formatPrice(s.priceMonthly)} ${t('admin.subscriptions.priceMonthlySuffix')}`
                  : '—'
              }
            />
            <DetailField
              label={t('admin.subscriptions.modal.priceYearly')}
              value={
                s.priceYearly !== null
                  ? `${formatPrice(s.priceYearly)} ${t('admin.subscriptions.priceYearlySuffix')}`
                  : '—'
              }
            />
            <DetailField
              label={t('admin.subscriptions.modal.bundleCode')}
              value={
                s.bundleCode
                  ? `${s.bundleCode}${s.appliedDiscountPercent ? ` (-${s.appliedDiscountPercent}%)` : ''}`
                  : '—'
              }
            />
            <DetailField
              label={t('admin.subscriptions.modal.createdAt')}
              value={formatDate(s.createdAt)}
            />
            <DetailField
              label={t('admin.subscriptions.modal.updatedAt')}
              value={formatDate(s.updatedAt)}
            />
            {s.gcrecord && (
              <DetailField
                label={t('admin.subscriptions.modal.gcrecord')}
                value={
                  <span className="text-red-600">
                    {formatDate(s.gcrecord)}{' '}
                    <Badge color="gray" className="ml-1">
                      {t('admin.subscriptions.deletedBadge')}
                    </Badge>
                  </span>
                }
              />
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

interface DetailFieldProps {
  label: string;
  value: ReactNode;
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span className="mt-0.5 text-text-primary">{value}</span>
    </div>
  );
}
